import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextAreaField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { WARRANTY_CLAIM_REJECTED_MESSAGE } from '../../constants';
import { useRejectWarrantyClaim } from '../hooks';
import { rejectWarrantyClaimSchema } from '../schemas';
import type { RejectWarrantyClaimValues } from '../types';

export type RejectWarrantyModalProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  /** Fired after a successful rejection, so the page can drop the row's actions and close the
   *  detail drawer. */
  onRejected: () => void;
};

/** "Reject Request" popup (Figma 366:310485), opened from the row kebab or the drawer footer. The
 *  reason is mandatory (AC3) and hard-capped at 2000 characters (AC4 — the cap is enforced by the
 *  input, so no over-limit error is ever shown). The title is a plain string: the shared overlay
 *  chrome already renders it as Funnel Display 24 Regular, which is the Figma leaf verbatim. */
export function RejectWarrantyModal({ open, id, onClose, onRejected }: RejectWarrantyModalProps) {
  const form = useForm<RejectWarrantyClaimValues>({
    resolver: zodResolver(rejectWarrantyClaimSchema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reject = useRejectWarrantyClaim(id);
  const toast = useToast();

  const submit = (values: RejectWarrantyClaimValues) =>
    reject.mutate(values, {
      onSuccess: () => {
        toast.success({ title: WARRANTY_CLAIM_REJECTED_MESSAGE });
        form.reset({ reason: '' });
        onRejected();
        onClose();
      },
      onError: (e) =>
        toast.error({
          title:
            e instanceof ApiError ? e.message : 'Could not reject the claim. Please try again.',
        }),
    });

  return (
    <FormModal
      form={form}
      open={open}
      onClose={onClose}
      size="lg"
      centered={false}
      title="Reject Request"
      submitText="Reject"
      onSubmit={submit}
      loading={reject.isPending}
    >
      <TextAreaField
        name="reason"
        control={form.control}
        label="Enter Reason"
        placeholder="Enter"
        rows={5}
        maxLength={2000}
        // Figma 366:310498 reads `0/2000`; antd's default count formatter spaces the slash.
        showCount={{ formatter: ({ count, maxLength }) => `${count}/${maxLength}` }}
        required
      />
    </FormModal>
  );
}
